import * as JSPM_V3 from "jsprintmanager-3";
import * as JSPM_V4 from 'jsprintmanager-4';
import * as JSPM_V8 from 'jsprintmanager-8';
import { GET } from '../api/axios';
import { ServiceEndPoints } from './constants/ApiConstant';
import { Base64 } from 'js-base64';
import { getCustomerAccountID } from './helperFunctions';

let JSPM, license_url;


let isJSPMStarting = false;
let jspmStartWatchdogTimer = null;
let jspmStatusSubscribers = [];

const isJSPMConnected = () => {
  return JSPM?.JSPrintManager?.WS?.status === 0;
};

const addJSPMStatusSubscriber = (onStatusUpdate) => {
  if (typeof onStatusUpdate === 'function' && !jspmStatusSubscribers.includes(onStatusUpdate)) {
    jspmStatusSubscribers.push(onStatusUpdate);
  }
};

const removeJSPMStatusSubscriber = (onStatusUpdate) => {
  if (typeof onStatusUpdate !== 'function') {
    return;
  }

  jspmStatusSubscribers = jspmStatusSubscribers.filter((callback) => callback !== onStatusUpdate);
};

const notifyJSPMStatusSubscribers = (status) => {
  jspmStatusSubscribers.forEach((callback) => {
    if (typeof callback === 'function') {
      callback(status);
    }
  });
};

const updateJSPMDisconnectedStatus = (dispatch, actions, onStatusUpdate) => {
  if (dispatch && actions?.setJSPMStatus) {
    dispatch(actions.setJSPMStatus(false));
  }

  notifyJSPMStatusSubscribers(false);
};

const attachSafeJSPMWebSocketErrorHandler = (dispatch, actions, onStatusUpdate) => {
  const wsRef = JSPM?.JSPrintManager?.WS;
  if (!wsRef) {
    return;
  }

  wsRef.onError = () => {
    updateJSPMDisconnectedStatus(dispatch, actions, onStatusUpdate);
  };
};

const attachJSPMWebSocketStatusHandlers = (dispatch, actions) => {
  if (!JSPM?.JSPrintManager?.WS) {
    return;
  }

  JSPM.JSPrintManager.WS.onStatusChanged = function () {
    const status = JSPM.JSPrintManager.WS.status === 0;
    if (status) {
      isJSPMStarting = false;
      if (jspmStartWatchdogTimer) {
        clearTimeout(jspmStartWatchdogTimer);
        jspmStartWatchdogTimer = null;
      }
    }
    dispatch(actions.setJSPMStatus(status));

    notifyJSPMStatusSubscribers(status);

    if (status && actions?.setAvailablePrinter) {
      getAvaiablePrinters()
        .then((printerList) => {
          dispatch(actions.setAvailablePrinter(printerList));
        })
        .catch(() => {
          dispatch(actions.setAvailablePrinter([]));
        });
    } else if (actions?.setAvailablePrinter) {
      dispatch(actions.setAvailablePrinter([]));
    }
  };

  JSPM.JSPrintManager.WS.onClose = function () {
    const status = JSPM.JSPrintManager.WS.status === 0;
    dispatch(actions.setJSPMStatus(status));
    notifyJSPMStatusSubscribers(status);
  };
};


export const updateJSPMReference = (jspmVersion) => {
  const normalizedVersion = String(jspmVersion ?? '').trim();
  console.log("jspmVersion::: ", normalizedVersion)
  if (normalizedVersion === "3") {
    console.log("inside 3")
    JSPM = JSPM_V3;
    license_url = ServiceEndPoints.getJSPMLicense_v3;
  } else if(normalizedVersion === "4") {
    console.log("inside 4")
    JSPM = JSPM_V4;
    license_url = ServiceEndPoints.getJSPMLicense_v4;
  } else {
    console.log("inside 8")
    JSPM = JSPM_V8;
    license_url = ServiceEndPoints.getJSPMLicense_v8;
  }
};

export const getJSPMStatus = (dispatch, actions, onStatusUpdate, isPrinterConfig = false) => {
  addJSPMStatusSubscriber(onStatusUpdate);

  if (!JSPM) {
    JSPM = JSPM_V3;
    license_url = ServiceEndPoints.getJSPMLicense_v3;
  }
  console.log('JSPM', JSPM);
  console.log('license_url', license_url);
  JSPM.JSPrintManager.license_url = license_url;

// Important: keep false to avoid repeated WebSocket retry errors
JSPM.JSPrintManager.auto_reconnect = false;

// JSPrintManager default handler throws raw Event objects on connection failures.
// Override with a safe handler so offline JSPM does not crash UI runtime.
attachSafeJSPMWebSocketErrorHandler(dispatch, actions, onStatusUpdate);
attachJSPMWebSocketStatusHandlers(dispatch, actions);

// If already connected, update status and do not start again
if (isJSPMConnected()) {
  dispatch(actions.setJSPMStatus(true));

  notifyJSPMStatusSubscribers(true);

    return () => removeJSPMStatusSubscriber(onStatusUpdate);
}

// Prevent duplicate start calls
if (isJSPMStarting) {
  const status = isJSPMConnected();
  if (status) {
    isJSPMStarting = false;
    if (jspmStartWatchdogTimer) {
      clearTimeout(jspmStartWatchdogTimer);
      jspmStartWatchdogTimer = null;
    }
  }
  dispatch(actions.setJSPMStatus(status));
  notifyJSPMStatusSubscribers(status);
  return () => removeJSPMStatusSubscriber(onStatusUpdate);
}

isJSPMStarting = true;

if (jspmStartWatchdogTimer) {
  clearTimeout(jspmStartWatchdogTimer);
}

// Some JSPM environments keep start() pending when the local service is OFF.
// This watchdog guarantees future getJSPMStatus calls can retry after a short window.
jspmStartWatchdogTimer = setTimeout(() => {
  if (!isJSPMConnected()) {
    isJSPMStarting = false;
    updateJSPMDisconnectedStatus(dispatch, actions, onStatusUpdate);
  }
  jspmStartWatchdogTimer = null;
}, 5000);

try {
  const startResult = JSPM.JSPrintManager.start();
  attachSafeJSPMWebSocketErrorHandler(dispatch, actions, onStatusUpdate);
  attachJSPMWebSocketStatusHandlers(dispatch, actions);

  if (startResult && typeof startResult.then === 'function') {
    startResult
      .then(() => {
        const status = isJSPMConnected();
        dispatch(actions.setJSPMStatus(status));

        notifyJSPMStatusSubscribers(status);
      })
      .catch(() => {
        updateJSPMDisconnectedStatus(dispatch, actions, onStatusUpdate);
      })
      .finally(() => {
        isJSPMStarting = false;
        if (jspmStartWatchdogTimer) {
          clearTimeout(jspmStartWatchdogTimer);
          jspmStartWatchdogTimer = null;
        }
      });
  } else {
    const status = isJSPMConnected();
    dispatch(actions.setJSPMStatus(status));

    notifyJSPMStatusSubscribers(status);

    isJSPMStarting = false;
    if (jspmStartWatchdogTimer) {
      clearTimeout(jspmStartWatchdogTimer);
      jspmStartWatchdogTimer = null;
    }
  }
} catch (error) {
  updateJSPMDisconnectedStatus(dispatch, actions, onStatusUpdate);
  isJSPMStarting = false;
  if (jspmStartWatchdogTimer) {
    clearTimeout(jspmStartWatchdogTimer);
    jspmStartWatchdogTimer = null;
  }
}

  // Immediately check current status and call callback
  if (isPrinterConfig) {
    const versionFound = ['v3', 'v4', 'v8'].find((v) => license_url?.includes(v));
    if (versionFound) {
      const initialStatus = JSPM.JSPrintManager.WS.status === 0;
      dispatch(actions.setJSPMStatus(initialStatus));
      notifyJSPMStatusSubscribers(initialStatus);
    }
  }

  attachJSPMWebSocketStatusHandlers(dispatch, actions);
  return () => removeJSPMStatusSubscriber(onStatusUpdate);
};

export const printZplLabels = (printData, selectedPrinter) => {
  if (!isJSPMConnected()) {
    console.warn('JSPrintManager is not connected. ZPL print skipped.');
    return;
  }

  var cpj = new JSPM.ClientPrintJob();
  cpj.clientPrinter = selectedPrinter ? new JSPM.InstalledPrinter(selectedPrinter) : 0;
  cpj.printerCommands = Base64.decode(printData);
  cpj.printerCommandsCodePage = -1;
  cpj.sendToClient();
};



export const printPDF = (printData, selectedPrinter, type) => {
  if (!isJSPMConnected()) {
    console.warn('JSPrintManager is not connected. PDF print skipped.');
    return;
  }

  let cpj = new JSPM.ClientPrintJob();
  cpj.clientPrinter = selectedPrinter ? new JSPM.InstalledPrinter(selectedPrinter) : 0;

  if (printData) {
    let myPdfFile;
    myPdfFile = new JSPM.PrintFilePDF(printData, JSPM.FileSourceType.Base64, `${type}.pdf`, 1);
    myPdfFile.pageSizing = JSPM.Sizing.None;
    myPdfFile.printRotation = JSPM.PrintRotation.Rot180;
    cpj.files.push(myPdfFile);
  }

  cpj.sendToClient();
};


export const downloadPDF = (base64, lableName, shipmentData, index) => {
  if (base64) {
    let shipmentid
    let blob = convertBase64toBlob(base64, 'application/pdf');
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    if (shipmentData?.labelResponse && shipmentData?.labelResponse.length > 0) {
      if (index !== undefined) {
        shipmentid = shipmentData.labelResponse[index]?.shipmentId ?? index + 1;
      }
      else {
        shipmentData?.labelResponse.map((id) => {
          shipmentid = id.shipmentId
        })
      }
    }
    else if (shipmentData?.customsInvoiceResponse && shipmentData?.customsInvoiceResponse.length > 0) {
      if (index !== undefined) {
        shipmentid = shipmentData.customsInvoiceResponse[index]?.shipmentId ?? index + 1;
      }
      else {
        shipmentid = shipmentData?.customsInvoiceResponse[0]?.shipmentId
      }
    }
    else if (shipmentData.length > 0) {
      shipmentid = shipmentData[0]?.labelResponse[0]?.shipmentId ?? shipmentData[0]?.customsInvoiceResponse[0]?.shipmentId;
    }
    else if (shipmentData?.shipmentId) {
      shipmentid = shipmentData.shipmentId
    }
    else if (shipmentData?.shipments[0].shipmentId) {
      shipmentid = shipmentData?.shipments[0].shipmentId
    }
    else {
      shipmentid = ""
    }
    link.href = blobUrl;
    link.download = shipmentid + lableName;
    document.body.appendChild(link);
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: false,
        cancelable: true,
        view: window
      })
    );
  }
}

const convertBase64toBlob = (content, contentType) => {
  contentType = contentType || '';
  var sliceSize = 512;
  var byteCharacters = window.atob(content);
  var byteArrays = [
  ];
  for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    var slice = byteCharacters.slice(offset, offset + sliceSize);
    var byteNumbers = new Array(slice.length);
    for (var i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    var byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  var blob = new Blob(byteArrays, {
    type: contentType
  });
  return blob;
}


export const getAvaiablePrinters = () => {
  if (!isJSPMConnected() || typeof JSPM?.JSPrintManager?.getPrinters !== 'function') {
    return Promise.resolve([]);
  }

  try {
    const printersResult = JSPM.JSPrintManager.getPrinters();
    if (printersResult && typeof printersResult.then === 'function') {
      return printersResult;
    }
    return Promise.resolve(Array.isArray(printersResult) ? printersResult : []);
  } catch (error) {
    return Promise.resolve([]);
  }
};

export const getPrintConfiguration = (dispacth, actions, userDetails) => {
  if (userDetails) {
    GET(`${ServiceEndPoints.printConfiguration}?userId=${userDetails.emailId}`)
      .then((response) => {
        dispacth(actions.setPrinterConfiguration(response.data));
      }).catch((err) => {
        dispacth(actions.setPrinterConfiguration({ userId: userDetails.emailId }));
      });
    GET(`${ServiceEndPoints.getOrderDefaults}?organization=${getCustomerAccountID()}`).then(res => {
      dispacth(actions.setPrinterConfiguration({
        "printerQueueDefault": res.data.printerQueueDefaults?.printerQueueDefault,
        "printerQueue": res.data.printerQueueDefaults?.printerQueue
      }))
    })
  }
};

export const getCurrentJSPM = () => JSPM;

export const printPDFForA4TypeLabel = (printData, selectedPrinter, type) => {
  if (!isJSPMConnected()) {
    console.warn('JSPrintManager is not connected. A4 PDF print skipped.');
    return;
  }

  let cpj = new JSPM.ClientPrintJob();
  cpj.clientPrinter = selectedPrinter ? new JSPM.InstalledPrinter(selectedPrinter) : 0;

  if (printData) {
    let myPdfFile;
    myPdfFile = new JSPM.PrintFilePDF(printData, JSPM.FileSourceType.Base64, `${type}.pdf`, 1);
    myPdfFile.pageSizing = JSPM.Sizing.Fit;
    myPdfFile.printRotation = JSPM.PrintRotation.Rot180;
    cpj.files.push(myPdfFile);
  }

  cpj.sendToClient();
};
